import React from 'react';
import clsx from 'clsx';
import { UserGroupIcon } from '@heroicons/react/16/solid';
import { ReservationReturn } from '../clients/rust-api-client';
import { formatMoney } from '../utils/billingHelpers';
import { ReservationStatusIcon } from './ReservationStatusIcon';
import {
  formatLocalRange,
  isZeroMoney,
  shortRef,
  statusLabel,
} from './reservationMeta';

const Chip: React.FC<{ className?: string; children: React.ReactNode; title?: string }> = ({
  className,
  children,
  title,
}) => (
  <span
    title={title}
    className={clsx(
      'inline-flex max-w-[14rem] items-center gap-1 truncate rounded-full border border-line px-2 py-0.5 text-[12px] leading-4 text-fg-muted',
      className
    )}
  >
    {children}
  </span>
);

/**
 * A reservation as one dense line, in the work-order row's grammar:
 * status, reference, amenity and home, then the facts as chips and the
 * booked time last — in the amenity's own wall-clock time.
 *
 * Like the work-order row, the row is not a button itself: a whole-row
 * "open" button sits underneath so the row reads as a list item to a
 * screen reader.
 */
export const ReservationRow: React.FC<{
  reservation: ReservationReturn;
  onOpen: (r: ReservationReturn) => void;
  /** Hide the home (a home's own list). */
  hideHome?: boolean;
}> = ({ reservation: r, onOpen, hideHome = false }) => {
  const ref = shortRef(r.id);
  const when = formatLocalRange(r.start_local, r.end_local, new Date().getFullYear());
  const fee = isZeroMoney(r.fee_amount) ? null : formatMoney(r.fee_amount);

  return (
    <li className="group relative flex items-center gap-3 px-4 py-2 text-[13px] hover:bg-fg/[0.03] has-[>button:focus-visible]:bg-fg/5">
      <button
        type="button"
        onClick={() => onOpen(r)}
        className="absolute inset-0 focus:outline-none"
      >
        <span className="sr-only">
          {`Open ${ref}: ${r.amenity_name}, ${r.home_address}, ${when}. ${statusLabel(r.status)}`}
        </span>
      </button>
      <span title={statusLabel(r.status)} className="pointer-events-none inline-flex">
        <ReservationStatusIcon status={r.status} />
      </span>
      <span className="pointer-events-none hidden w-[76px] shrink-0 font-mono text-[12px] text-fg-muted sm:inline">
        {ref}
      </span>
      <span className="pointer-events-none min-w-0 flex-1 truncate">
        <span className="font-medium text-fg">{r.amenity_name}</span>
        {!hideHome && <span className="text-fg-muted"> · {r.home_address}</span>}
      </span>
      <span className="pointer-events-none hidden items-center gap-1.5 md:flex">
        {r.party_size > 1 && (
          <Chip title="Party size">
            <UserGroupIcon className="h-3 w-3" aria-hidden />
            {r.party_size}
          </Chip>
        )}
        {fee && <Chip title="Fee">{fee}</Chip>}
      </span>
      <span className="pointer-events-none hidden shrink-0 text-right text-[12px] tabular-nums text-fg-muted sm:inline">
        {when}
      </span>
    </li>
  );
};

export default ReservationRow;
